import { createApi } from '@reduxjs/toolkit/query/react'
import baseQuery from 'services'
import {
  ITender,
  ITenderNotificationsPoll,
  ITenderOverview,
  ITenders,
  ITendersControlPanelSummary,
} from 'types/tender'
import { IPaginationQueryInfo } from 'types/pagination'
import { IdParams } from 'types/api'
import { v4 as uuidv4 } from 'uuid'
import { toast } from 'react-toastify'
import { translate } from 'hoc/Translate'
import { ITenderDashboardData } from 'types/tender/tenderDashboard'
import { ITenderClientOverviews } from 'types/tender/tenderClientOverview'

export enum ProcurementMethods {
  OPEN_TENDER = 'OPEN_TENDER',
  PRICE_INQUIRY = 'PRICE_INQUIRY',
  TWO_STAGE_TENDER = 'TWO_STAGE_TENDER',
}

type TenderSearchFilters = {
  name?: string
  orgName?: string
  startFrom?: string | null
  startUntil?: string | null
  status?: Array<String>
  method?: keyof typeof ProcurementMethods
}

type TendersSearchQueries = IPaginationQueryInfo & TenderSearchFilters
type TendersClientOverviewQueries = IPaginationQueryInfo & { participating: boolean }
type IModifyTenderParams = IdParams & { changeReason: string }
type TendersCancelParam = IdParams & { cancelReason: string }

export const tenderApi = createApi({
  reducerPath: 'tender',
  tagTypes: [
    'Tenders',
    'TenderById',
    'TendersControlPanel',
    'TendersOverview',
    'TendersClientOverview',
    'TendersDashboardData',
    'TenderNotifications',
  ],
  baseQuery,
  endpoints: (builder) => ({
    tenders: builder.query<ITenders, IPaginationQueryInfo>({
      query: ({ page, size }) => {
        return {
          url: '/tenders',
          method: 'get',
          params: {
            page,
            size,
          },
        }
      },
      providesTags: ['Tenders'],
    }),
    tendersSearch: builder.query<ITenders, TendersSearchQueries>({
      query: ({
        page,
        size,
        name,
        orgName,
        startFrom,
        startUntil,
        status,
        method,
      }) => {
        const params = new URLSearchParams({
          page: page.toString(),
          size: size.toString(),
        })

        if (status && status.length) {
          ;[status].forEach((status) => params.append('status', status.toString()))
        }

        const searchByName = name?.trim()
        if (searchByName && searchByName.length > 0) {
          params.set('name', searchByName)
        }

        const searchByOrgName = orgName?.trim()
        if (searchByOrgName && searchByOrgName.length > 0) {
          params.set('orgName', searchByOrgName)
        }

        if (startFrom) {
          params.set('startFrom', startFrom)
        }
        if (startUntil) {
          params.set('startUntil', startUntil)
        }
        if (method) {
          params.set('method', method)
        }

        return {
          url: '/tenders/search',
          method: 'get',
          params,
        }
      },
      providesTags: ['Tenders'],
    }),
    tendersOverviewSearch: builder.query<ITenders, TendersSearchQueries>({
      query: ({
        page,
        size,
        name,
        orgName,
        startFrom,
        startUntil,
        status,
        method,
      }) => {
        const params = new URLSearchParams({
          page: page.toString(),
          size: size.toString(),
        })

        if (status) {
          ;[status].forEach((status) => params.append('status', status.toString()))
        }

        const searchByName = name?.trim()
        if (searchByName && searchByName.length > 0) {
          params.set('name', searchByName)
        }

        const searchByOrgName = orgName?.trim()
        if (searchByOrgName && searchByOrgName.length > 0) {
          params.set('orgName', searchByOrgName)
        }

        if (startFrom) {
          params.set('startFrom', startFrom)
        }
        if (startUntil) {
          params.set('startUntil', startUntil)
        }
        if (method) {
          params.set('method', method)
        }

        return {
          url: '/tenders/overview',
          method: 'get',
          params,
        }
      },
      providesTags: ['TendersOverview'],
    }),

    tender: builder.query<ITender, IdParams>({
      query: ({ id }) => `/tenders/${id}`,
      providesTags: ['TenderById'],
      keepUnusedDataFor: 10000,
    }),

    tendersControlPanel: builder.query<ITendersControlPanelSummary, IdParams>({
      query: ({ id }) => `/tenders/${id}/control-panel-summary`,
      providesTags: ['TendersControlPanel'],
    }),

    tenderOverview: builder.query<ITenderOverview, IdParams>({
      query: ({ id }) => `/tenders/overview/${id}`,
    }),

    deleteTender: builder.mutation<void, IdParams>({
      query: ({ id }) => ({
        url: `/tenders/${id}`,
        method: 'delete',
      }),
      invalidatesTags: ['Tenders'],
    }),

    publishTender: builder.mutation<void, IdParams>({
      query: ({ id }) => ({
        url: `/tenders/${id}/publish`,
        method: 'post',
      }),
      invalidatesTags: ['Tenders'],
    }),
    modifyTender: builder.mutation<void, IModifyTenderParams>({
      query: ({ id, changeReason }) => ({
        url: `/tenders/${id}/modify`,
        method: 'post',
        body: { changeReason },
      }),
      invalidatesTags: ['Tenders'],
    }),

    cancelTender: builder.mutation<void, TendersCancelParam>({
      query: (body) => ({
        url: `/tenders/${body.id}/cancel`,
        method: 'post',
        body,
      }),
      invalidatesTags: ['Tenders'],
    }),

    createTender: builder.mutation<ITender, Partial<ITender>>({
      query: (body) => ({
        url: '/tenders',
        method: 'post',
        body: {
          ...body,
          id: body.id || uuidv4(),
        },
      }),
      invalidatesTags: ['Tenders', 'TenderById'],
      transformResponse: (res: ITender) => {
        toast.success(translate('operation-success'))

        return res
      },
    }),

    tendersDashboardData: builder.query<ITenderDashboardData, void>({
      query: () => `/tenders/dashboard-data`,
      providesTags: ['TendersDashboardData'],
    }),

    tendersClientOverview: builder.query<
      ITenderClientOverviews,
      TendersClientOverviewQueries
    >({
      query: ({ page, size, participating }) => {
        return {
          url: '/tenders/client-overview',
          method: 'get',
          params: {
            page,
            size,
            participating,
          },
        }
      },
      providesTags: ['TendersClientOverview'],
    }),
    notificationsPoll: builder.query<ITenderNotificationsPoll, { tenderId: string }>(
      {
        query: ({ tenderId }) => {
          return {
            url: `/tenders/${tenderId}/notifications/poll`,
            method: 'get',
            params: {
              tenderId,
            },
          }
        },
        providesTags: ['TenderNotifications'],
      }
    ),
  }),
})

export const {
  useTendersQuery,
  useTendersSearchQuery,
  useTendersOverviewSearchQuery,
  useCreateTenderMutation,
  useDeleteTenderMutation,
  useTenderQuery,
  useTenderOverviewQuery,
  useCancelTenderMutation,
  usePublishTenderMutation,
  useTendersControlPanelQuery,
  useTendersDashboardDataQuery,
  useTendersClientOverviewQuery,
  useNotificationsPollQuery,
  useModifyTenderMutation,
} = tenderApi
